import React from 'react';
import _ from 'lodash';
import { OZEMPIC_DESC, PRESCRIPTION_CANONICAL } from '../../constants/defaults';

export const getLdJson = (
  drugName,
  drugInfo,
  drugStructure,
  lowestPrice,
  replaceableVariables,
  seoPages
) => {
  let drugDisplayName = drugName;
  if (drugInfo && drugInfo.cmsData && !_.isEmpty(drugInfo.cmsData.name)) {
    drugDisplayName = drugInfo?.cmsData?.name;
  }
  let structuredData;
  let genericName = '';
  const medicalReviewer =
    drugInfo && drugInfo.cmsData && drugInfo.cmsData.medicalReviewer
      ? drugInfo.cmsData.medicalReviewer
      : {};
  if (
    drugStructure &&
    !_.isEmpty(drugStructure.defaultKey) &&
    drugStructure.defaultKey.isGeneric
  ) {
    genericName = drugStructure.defaultKey.name;
  }
  let desc = '';
  // Helper to convert spaces to hyphens for @id values
  const hyphenate = str =>
    typeof str === 'string' ? str.replace(/\s+/g, '-') : '';

  const getDescription = () => {
    if (drugInfo?.cmsData?.name === 'Ozempic') {
      return OZEMPIC_DESC;
    } else {
      if (!_.isEmpty(drugInfo?.cmsData?.description)) {
        const descriptionContent = drugInfo?.cmsData?.description?.content;
        if (Array.isArray(descriptionContent)) {
          for (const item of descriptionContent) {
            if (item.nodeType === 'paragraph') {
              const text = Array.isArray(item.content)
                ? item.content
                    .map(c => c.value?.trim())
                    .filter(Boolean)
                    .join(' ')
                : '';
              if (text) {
                // Extract the first sentence from the description
                const plainText = text.replace(/<[^>]+>/g, ' ');
                // Split by period, exclamation, or question mark followed by space or end of string
                const match = plainText.match(/.*?[.!?](\s|$)/);
                desc = match ? match[0].trim() : plainText.trim();
                break;
              }
            }
          }
        }
      }
    }
    return desc;
  };

  structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': 'https://www.singlecare.com/#org',
        name: 'SingleCare',
        url: 'https://www.singlecare.com'
      },
      {
        '@type': 'Drug',
        '@id': `#drug-${drugName}`,
        name: `${drugDisplayName}`,
        url: `${PRESCRIPTION_CANONICAL}${drugName}`,
        drugClass: getDrugsClasses(drugInfo),
        prescriptionStatus: 'https://schema.org/PrescriptionOnly',
        administrationRoute: drugInfo?.drug?.form,
        nonProprietaryName:
          drugInfo?.cmsData?.name === 'Ozempic' ? 'Semaglutide' : genericName,
        description: getDescription(),
        image: drugInfo?.imageUrl,
        relatedDrug: getRelatedDrugs(drugInfo)
      },
      {
        '@type': 'Product',
        '@id': `${PRESCRIPTION_CANONICAL}${drugName}#product`,
        name: `${drugDisplayName}`,
        url: `${PRESCRIPTION_CANONICAL}${drugName}`,
        image: drugInfo?.imageUrl,
        description: `${drugDisplayName}, ${replaceableVariables?.dosageCoupon} ${replaceableVariables?.formCoupon}`,
        additionalProperty: [
          {
            '@type': 'PropertyValue',
            name: 'Strength',
            value: replaceableVariables?.dosageCoupon
          },
          {
            '@type': 'PropertyValue',
            name: 'Dosage form',
            value: replaceableVariables?.formCoupon
          }
        ],
        offers: { '@id': `${PRESCRIPTION_CANONICAL}${drugName}#coupon-offer` }
      },
      {
        '@type': ['Product', 'Service'],
        '@id': 'https://www.singlecare.com/#discount-card',
        name: 'SingleCare Prescription Discount Card',
        url: 'https://www.singlecare.com',
        description: 'Free discount card service for prescription savings.',
        provider: { '@id': 'https://www.singlecare.com/#org' },
        offers: [
          {
            '@type': 'Offer',
            '@id': 'https://www.singlecare.com/#card-offer',
            url: 'https://www.singlecare.com',
            price: '0',
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            itemOffered: {
              '@id': 'https://www.singlecare.com/#discount-card'
            },
            seller: { '@id': 'https://www.singlecare.com/#org' }
          },
          {
            '@type': 'Offer',
            '@id': `${PRESCRIPTION_CANONICAL}${drugName}#coupon-offer`,
            url: `${PRESCRIPTION_CANONICAL}${drugName}`,
            price: lowestPrice,
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: lowestPrice,
              priceCurrency: 'USD',
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                value: replaceableVariables?.qtyCoupon,
                unitText: replaceableVariables?.formCoupon
              }
            },
            availability: 'https://schema.org/InStock',
            itemOffered: {
              '@id': `${PRESCRIPTION_CANONICAL}${drugName}#product`
            },
            seller: { '@id': 'https://www.singlecare.com/#org' }
          }
        ]
      },
      {
        '@type': 'Person',
        '@id': medicalReviewer.name
          ? `https://www.singlecare.com/#author-${hyphenate(
              medicalReviewer.name
            )}`
          : '',
        name: medicalReviewer.name
          ? `${medicalReviewer.name}, ${
              medicalReviewer?.qualifications
                ? medicalReviewer?.qualifications
                : ''
            }`
          : '',
        jobTitle: medicalReviewer?.title ? medicalReviewer.title : '',
        description: (() => {
          try {
            const bioJson = medicalReviewer.biography
              ? JSON.parse(medicalReviewer.biography)
              : '';
            // Extract plain text from Contentful rich text JSON
            const extractText = node => {
              if (!node) return '';
              if (typeof node === 'string') return node;
              if (Array.isArray(node)) return node.map(extractText).join(' ');
              if (node.nodeType === 'text') return node.value || '';
              if (node.content) return extractText(node.content);
              return '';
            };
            return extractText(bioJson).replace(/\s+/g, ' ').trim();
          } catch (e) {
            return '';
          }
        })()
      },
      {
        '@type': 'MedicalWebPage',
        '@id': `${PRESCRIPTION_CANONICAL}${drugName}#webpage`,
        url: `${PRESCRIPTION_CANONICAL}${drugName}`,
        relatedLink: Array.isArray(seoPages)
          ? seoPages.map(
              page =>
                `https://www.singlecare.com${
                  page && page.link ? page.link : ''
                }`
            )
          : [],
        name: `${drugDisplayName} prescription savings & price information`,
        about: {
          '@id': `#drug-${drugName}`
        },
        primaryImageOfPage: drugInfo?.imageUrl,
        publisher: { '@id': 'https://www.singlecare.com/#org' },
        author: {
          '@id': `https://www.singlecare.com/#author-${hyphenate(
            medicalReviewer.name
          )}`
        },
        breadcrumb: {
          '@id': `${PRESCRIPTION_CANONICAL}${drugName}#breadcrumb`
        },
        description: getDescription(),
        audience: {
          '@type': 'Audience',
          audienceType:
            'Patients and caregivers seeking prescription savings in the United States'
        }
      },
      {
        '@type': 'FAQPage',
        '@id': `${PRESCRIPTION_CANONICAL}${drugName}#faq`,
        url: `${PRESCRIPTION_CANONICAL}${drugName}`,
        name: `${drugDisplayName} FAQs`,
        about: { '@id': `#drug-${drugName}` },
        publisher: { '@id': 'https://www.singlecare.com/#org' },
        author: {
          '@id': `https://www.singlecare.com/#author-${hyphenate(
            medicalReviewer.name
          )}`
        },
        breadcrumb: {
          '@id': `${PRESCRIPTION_CANONICAL}${drugName}#breadcrumb`
        },
        mainEntity: getFAQS(drugInfo?.cmsData?.faqs)
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${PRESCRIPTION_CANONICAL}${drugName}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Homepage',
            item: 'https://www.singlecare.com/'
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: drugDisplayName,
            item: `${PRESCRIPTION_CANONICAL}${drugName}`
          }
        ]
      }
    ]
  };
  return JSON.stringify(cleanObjectRecursive(structuredData));
};

/**
 * Recursively cleans an object by removing fields that are null or empty strings.
 * @param {any} data - The object or array to clean.
 * @returns {any} The cleaned data.
 */
function cleanObjectRecursive(data) {
  if (Array.isArray(data)) {
    return data.map(cleanObjectRecursive);
  }

  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    return Object.entries(data).reduce((acc, [key, value]) => {
      const cleanedValue = cleanObjectRecursive(value);
      if (cleanedValue !== null && cleanedValue !== '') {
        acc[key] = cleanedValue;
      }
      return acc;
    }, {});
  }

  return data;
}

const getDrugsClasses = drugInfo => {
  let drugClasses = [];
  if (!_.isEmpty(drugInfo?.monoGraphData?.therapeuticClasses)) {
    drugInfo.monoGraphData.therapeuticClasses.forEach(function (classItem) {
      let DrugClass = {
        '@type': 'DrugClass',
        name: classItem
      };
      drugClasses.push(DrugClass);
    });
  } else if (!_.isEmpty(drugInfo?.cmsData?.drugClasses)) {
    drugInfo?.cmsData?.drugClasses.forEach(item => {
      let DrugClass = {
        '@type': 'DrugClass',
        name: item?.name
      };
      drugClasses.push(DrugClass);
    });
  }
  return drugClasses;
};

const getFAQS = faqs => {
  const mainEntity = [];
  let currentQuestion = null;

  faqs?.content?.forEach(item => {
    const nodeType = item.nodeType;

    if (nodeType === 'heading-3') {
      // Extract question text (bold text in heading)
      const questionText = item.content
        .map(c => c.value?.trim())
        .filter(Boolean)
        .join(' ');

      if (questionText) {
        currentQuestion = {
          '@type': 'Question',
          name: questionText,
          acceptedAnswer: {
            '@type': 'Answer',
            text: ''
          }
        };
        mainEntity.push(currentQuestion);
      }
    } else if (nodeType === 'paragraph' && currentQuestion) {
      // Append paragraph text to the current answer
      const paragraphText = item.content
        .map(c => c.value?.trim())
        .filter(Boolean)
        .join(' ');

      if (paragraphText) {
        if (currentQuestion.acceptedAnswer.text.length > 0) {
          currentQuestion.acceptedAnswer.text += ' ';
        }
        currentQuestion.acceptedAnswer.text += paragraphText;
      }
    }
  });

  return mainEntity;
};

const getRelatedDrugs = drugInfo => {
  let relatedDrugs = [];
  if (drugInfo && drugInfo.relatedDrugs) {
    drugInfo.relatedDrugs.forEach(function (relatedDrug) {
      let relatedItem = {
        '@type': 'Drug',
        name: relatedDrug.drugName,
        url: `${PRESCRIPTION_CANONICAL}${relatedDrug.seoName}`
      };
      relatedDrugs.push(relatedItem);
    });
  }
  return relatedDrugs;
};

export const setMetaContent = drugInfo => {
  if (drugInfo && drugInfo.cmsData && drugInfo.cmsData.metaRobotsNoindex)
    return (
      <meta name="ROBOTS" content="NOINDEX, FOLLOW, NOSNIPPET, NOODP, NOYDIR" />
    );
};
